import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Minus,
  Plus,
  Star,
  ScanLine,
  Shield,
  Mail,
  Trash2,
  AlarmClock,
  Sparkles,
  Warehouse,
  Calculator,
  Activity,
  ClipboardList,
  FileText,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import BrowserFrame, { HOH_SCREENS } from "../components/site/BrowserFrame";

// One icon per entry in t.hoh.features, same order.
const featureIcons = [
  Star,
  ScanLine,
  Shield,
  Mail,
  Trash2,
  AlarmClock,
  Sparkles,
  Warehouse,
  Calculator,
  Activity,
  ClipboardList,
  FileText,
];

const HOH_SAVINGS_CHART = "/images/screens/hoh/savings-waterfall.png";
const HOH_LOGO = "/images/logos/heart-of-house.png";
const HOH_APP_URL = "https://heartofhouse.ca";

const POSITIVE = new Set(["yes", "oui"]);
const NEGATIVE = new Set(["no", "non", "—", ""]);

const cellIcon = (val) => {
  if (POSITIVE.has(val)) return <Check className="text-[#E8490F]" size={18} />;
  if (NEGATIVE.has(val)) return <Minus className="text-white/30" size={18} />;
  return <span className="text-white/60 text-xs italic">{val}</span>;
};

// One row of the Intelligence billing card: a native <details>, so it opens and closes without script.
const BillingRow = ({ title, defaultOpen, testId, children }) => (
  <details className="group border-t border-[#333333]" open={defaultOpen} data-testid={testId}>
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 md:px-8 py-5 font-medium text-white [&::-webkit-details-marker]:hidden">
      {title}
      <Plus
        size={20}
        className="flex-shrink-0 text-[#E8490F] transition-transform duration-200 group-open:rotate-45"
        aria-hidden
      />
    </summary>
    {children}
  </details>
);

const HeartOfHouse = () => {
  const { t } = useLanguage();
  const p = t.hoh;
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const prevTitle = document.title;
    const prevDesc = meta ? meta.getAttribute("content") : null;
    document.title = p.metaTitle;
    if (meta) meta.setAttribute("content", p.metaDescription);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [p.metaTitle, p.metaDescription]);

  const group = p.featureGroups[tab];

  return (
    <div data-testid="page-heart-of-house">
      {/* HERO */}
      <section className="border-b border-[#242424] relative overflow-hidden">
        <div
          className="pointer-events-none absolute -top-56 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-[#E8490F]/12 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative pt-16 md:pt-20 pb-16 md:pb-20 flex flex-col items-center text-center">
          <span
            className="inline-flex items-center gap-2 rounded-full border border-[#E8490F]/40 bg-[#E8490F]/10 px-4 py-1.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]"
            data-testid="hoh-launch-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E8490F] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E8490F]" />
            </span>
            {p.launchBadge}
          </span>

          <a
            href={HOH_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-9 block opacity-95 hover:opacity-100 transition-opacity"
            data-testid="hoh-hero-logo"
            data-track="hoh-hero-logo"
          >
            <img src={HOH_LOGO} alt={p.heading} className="h-24 sm:h-28 xl:h-32 w-auto max-w-full" />
          </a>

          <h1 className="mt-7 mise-serif mise-accent text-4xl sm:text-5xl md:text-6xl leading-[1.08] max-w-5xl">
            <span className="sr-only">{p.heading}. </span>
            {p.subheading}
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-3xl leading-relaxed">{p.intro}</p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/heart-of-house/demo" className="mise-pill-primary" data-testid="hoh-see-demo" data-track="hoh-book-demo">
              {p.bookDemoCta}
              <ArrowRight size={16} />
            </Link>
            <Link to="/heart-of-house/pilot" className="mise-pill-secondary" data-testid="hoh-request-pilot" data-track="hoh-pilot-hero">
              {t.common.requestPilot}
            </Link>
          </div>

          <BrowserFrame
            src={HOH_SCREENS.dashboard}
            alt={p.heading}
            testId="hoh-hero-screenshot"
            className="mt-14 w-full text-left"
          />

          <div className="mt-10 max-w-3xl" data-testid="hoh-integrations">
            <p className="text-xs font-medium text-white/50 uppercase tracking-wider">{p.integrationsLabel}</p>
            <ul className="mt-3 flex flex-wrap justify-center gap-2">
              {p.integrations.map((name) => (
                <li
                  key={name}
                  className="rounded-full border border-[#333333] bg-[#1a1a1a] px-3.5 py-1.5 text-[13px] text-white/80"
                >
                  {name}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-white/40 leading-relaxed">{p.integrationsNote}</p>
          </div>
        </div>
      </section>

      {/* INSIDE THE PLATFORM */}
      <section className="mise-section" data-testid="hoh-features">
        <div className="mise-container">
          <p className="mise-eyebrow">{p.featuresEyebrow}</p>
          <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
            {p.featuresHeading}
          </h2>

          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label={p.featuresEyebrow}>
            {p.featureGroups.map((g, i) => (
              <button
                key={g.name}
                type="button"
                role="tab"
                id={`hoh-tab-${i}`}
                aria-selected={tab === i}
                aria-controls="hoh-tabpanel"
                onClick={() => setTab(i)}
                className={`rounded-full border px-5 py-2.5 text-sm md:text-base font-medium transition-colors ${
                  tab === i
                    ? "border-[#E8490F] bg-[#E8490F]/12 text-white"
                    : "border-[#333333] bg-[#1a1a1a] text-white/70 hover:text-white hover:border-[#6a6a6a]"
                }`}
                data-testid={`hoh-feature-tab-${i}`}
                data-track={`hoh-tab-${i}`}
              >
                {g.name}
              </button>
            ))}
          </div>

          <div
            id="hoh-tabpanel"
            role="tabpanel"
            aria-labelledby={`hoh-tab-${tab}`}
            className="mt-8 grid lg:grid-cols-12 gap-10 items-center"
          >
            <div className="lg:col-span-5 min-w-0">
              <p className="text-lg text-white/70">{group.sub}</p>
              <ul className="mt-6 space-y-6">
                {group.items.map((i) => {
                  const Icon = featureIcons[i];
                  const f = p.features[i];
                  return (
                    <li key={i} className="flex gap-4" data-testid={`hoh-feature-${i}`}>
                      <Icon size={20} className="mt-0.5 flex-shrink-0 text-[#E8490F]" aria-hidden />
                      <div>
                        <h3 className="text-lg font-semibold text-white">{f.title}</h3>
                        <p className="mt-1 text-white/70 leading-relaxed">{f.text}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="lg:col-span-7 min-w-0">
              <BrowserFrame
                src={HOH_SCREENS[group.screen]}
                alt={group.caption}
                caption={group.caption}
                testId="hoh-feature-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PLANS AND PRICING */}
      <section className="mise-section border-t border-[#242424]">
        <div className="mise-container">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-end">
            <div className="lg:col-span-5 min-w-0">
              <p className="mise-eyebrow">{p.modelHeading}</p>
              <h2 className="mt-4 mise-serif mise-accent text-3xl md:text-4xl leading-snug">{p.modelLead}</h2>
            </div>
            <p className="lg:col-span-7 text-white/70 leading-relaxed text-lg">{p.modelBody}</p>
          </div>

          {/* Each card is a subgrid on md+, so flag, tag, name, price, billing line and
              description line up across all three cards whatever their length. */}
          <div className="mt-12 grid gap-5 md:grid-cols-3 md:gap-y-0" data-testid="hoh-plans">
            {p.plans.map((plan, i) => {
              const featured = i === 1;
              return (
                <div
                  key={plan.name}
                  className={`mise-card p-7 flex flex-col md:grid md:grid-rows-subgrid md:row-span-6 md:content-start ${
                    featured ? "!border-[#E8490F]" : ""
                  }`}
                  data-testid={`hoh-plan-${i}`}
                >
                  <div>
                    {featured && (
                      <span className="inline-block rounded-full bg-[#E8490F] px-3 py-1 text-[11px] font-semibold tracking-[0.14em] uppercase text-white">
                        {p.pilotPlanFlag}
                      </span>
                    )}
                  </div>
                  <p className="mise-eyebrow">{plan.tag}</p>
                  <h3 className="mt-2 text-xl font-semibold text-white">{plan.name}</h3>
                  <p className="mt-3 text-4xl font-semibold tracking-tight mise-accent">{plan.price}</p>
                  <p className="mt-1 text-xs text-white/50 leading-snug">{plan.per}</p>
                  <p className="mt-5 text-sm text-white/70 leading-relaxed">{plan.text}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-5 text-sm text-white/45 leading-relaxed">{p.modelFloorNote}</p>

          {/* How Intelligence billing works */}
          <div className="mt-10 mise-card overflow-hidden hover:!transform-none" data-testid="hoh-billing">
            <div className="px-6 md:px-8 py-6">
              <p className="mise-eyebrow">{p.billingHeading}</p>
              <p className="mt-2 text-white/70 leading-relaxed">{p.revCapNote}</p>
            </div>

            <BillingRow title={p.tiersHeading} defaultOpen testId="hoh-tiers">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#333333]">
                      <th className="px-6 md:px-8 py-3 text-xs font-medium text-white/50 uppercase tracking-wider">
                        {p.tiersCols[0]}
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-white/50 uppercase tracking-wider">
                        {p.tiersCols[1]}
                      </th>
                      <th className="px-5 py-3 text-xs font-medium text-white/50 uppercase tracking-wider">
                        {p.tiersCols[2]}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.tiers.map((row, i) => (
                      <tr key={row.tier} className="border-b border-[#2a2a2a] last:border-b-0" data-testid={`hoh-tier-${i}`}>
                        <td className="px-6 md:px-8 py-4 text-white font-medium">{row.tier}</td>
                        <td className="px-5 py-4 text-white/70">{row.when}</td>
                        <td className="px-5 py-4 text-[#E8490F] font-semibold text-lg whitespace-nowrap">{row.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </BillingRow>

            <BillingRow title={p.protectionsHeading} testId="hoh-protections">
              <ul className="px-6 md:px-8 pb-7 space-y-3">
                {p.protections.map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`hoh-protection-${i}`}>
                    <Check className="text-[#E8490F] flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-white/80 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </BillingRow>

            <BillingRow title={p.exampleHeading} testId="hoh-example">
              <div className="px-6 md:px-8 pb-8 grid lg:grid-cols-2 gap-8 items-center">
                <div className="min-w-0">
                  <div className="divide-y divide-[#333333]">
                    {p.exampleRows.map((row, i) => (
                      <div
                        key={i}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-1 sm:gap-4"
                      >
                        <span className={`text-base ${row.accent ? "text-white font-medium" : "text-white/70"}`}>
                          {row.label}
                        </span>
                        <span
                          className={`text-lg font-semibold tracking-tight whitespace-nowrap ${
                            row.accent ? "mise-accent" : "text-white"
                          }`}
                          data-testid={`hoh-example-value-${i}`}
                        >
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-5 text-xs text-white/40 leading-relaxed">{p.exampleNote}</p>
                </div>
                <figure className="min-w-0" data-testid="hoh-savings-chart">
                  <div className="rounded-2xl overflow-hidden bg-white">
                    <img src={HOH_SAVINGS_CHART} alt={p.chartAlt} loading="lazy" className="w-full h-auto" />
                  </div>
                  <figcaption className="mt-4 text-sm text-white/60 leading-relaxed">{p.chartCaption}</figcaption>
                </figure>
              </div>
            </BillingRow>
          </div>
        </div>
      </section>

      {/* COMPETITIVE COMPARISON */}
      <section className="mise-section border-t border-[#242424]">
        <div className="mise-container">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
            {p.compareHeading}
          </h2>
          <div className="mt-10 mise-card overflow-hidden hover:!transform-none" data-testid="hoh-compare-table">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[#333333] bg-[#1a1a1a]">
                    <th className="p-5 text-sm font-medium text-white/50 uppercase tracking-wider">&nbsp;</th>
                    {p.compareCols.map((c, i) => (
                      <th
                        key={c}
                        className={`p-5 text-sm font-semibold ${i === 0 ? "text-[#E8490F]" : "text-white/60"}`}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {p.compareRows.map((row, i) => (
                    <tr key={row.feature} className="border-b border-[#2a2a2a] last:border-b-0" data-testid={`hoh-compare-row-${i}`}>
                      <td className="p-5 text-white/85">{row.feature}</td>
                      {row.values.map((v, j) => (
                        <td key={j} className="p-5">
                          {cellIcon(v)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* PILOT CTA */}
      <section className="mise-section border-t border-[#242424]">
        <div className="mise-container">
          <div className="mise-card p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight">{p.pilotHeading}</h2>
              <p className="mt-3 text-white/70 text-lg leading-relaxed">{p.pilotSub}</p>
            </div>
            <Link
              to="/heart-of-house/pilot"
              className="mise-pill-primary self-start whitespace-nowrap"
              data-testid="hoh-cta-bottom"
              data-track="hoh-pilot-bottom"
            >
              {t.common.requestPilot}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HeartOfHouse;
